import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Typography } from "@mui/material";

// How long the post-copy confirmation stays visible before hiding again.
const COPY_CONFIRMATION_MS = 2000;

// The terminal panel for a 200 response (NOTIF-19/NOTIF-20). Never rendered
// for 400/500/unparseable (NOTIF-21) or a missing-HTTP-status outcome
// (NOTIF-22) — those stay on the Review step instead (wired in Composer.js).
export default function ResultPanel({ result, onComposeAnother }) {
  const [copied, setCopied] = useState(false);
  const { total_should_be_created: total, group_id: groupId } = result || {};
  const hasValidTotal = typeof total === "number" && Number.isFinite(total);
  const zeroRecipients = hasValidTotal && total === 0;

  useEffect(() => {
    if (!copied) {
      return undefined;
    }
    const timer = setTimeout(() => setCopied(false), COPY_CONFIRMATION_MS);
    return () => clearTimeout(timer);
  }, [copied]);

  const handleCopy = () => {
    if (!navigator.clipboard || !groupId) {
      return;
    }
    navigator.clipboard
      .writeText(groupId)
      .then(() => setCopied(true))
      .catch(() => {});
  };

  return (
    <Box data-testid="result-region" sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {!hasValidTotal ? (
        // Neither NOTIF-19 nor NOTIF-20 — the 200 body didn't give us a usable
        // count, so we can't claim either outcome instead of guessing one.
        <Alert severity="warning" data-testid="malformed-result">
          <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
            Recipient count unavailable
          </Typography>
          <Typography variant="body2">
            The request succeeded, but the response didn't include a recipient count, so this
            screen can't confirm the outcome. Check the group ID below if you need to verify
            status another way.
          </Typography>
        </Alert>
      ) : zeroRecipients ? (
        // NOTIF-20 — a distinct state: same 200 response, but explicitly not
        // framed as a successful send since nothing will go out.
        <Alert severity="warning" data-testid="zero-recipients-result">
          <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
            No recipients — nothing will be created
          </Typography>
          <Typography variant="body2">
            This audience resolved to zero recipients. No notification will be created for this
            send.
          </Typography>
        </Alert>
      ) : (
        // NOTIF-19 — "queued for delivery", never "sent"/"delivered"; the
        // count is labelled an estimate, plus the batching/no-polling caveats.
        <Box data-testid="recipients-result">
          <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
            Queued for delivery
          </Typography>
          <Typography variant="body2">Estimated recipients: {total}</Typography>
          <Typography variant="body2" color="text.secondary">
            This count is a pre-write projection taken before send — it can overstate the actual
            audience.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Delivery rolls out in batches of about 1,000 recipients, roughly 5 minutes apart. No
            progress can be polled.
          </Typography>
        </Box>
      )}

      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="body2">Group ID: {groupId || "not provided"}</Typography>
        <Button size="small" onClick={handleCopy}>
          Copy group ID
        </Button>
        {copied && (
          <Typography variant="body2" color="success.main">
            Copied to clipboard
          </Typography>
        )}
      </Box>

      <Box>
        <Button variant="contained" onClick={onComposeAnother}>
          Compose another
        </Button>
      </Box>
    </Box>
  );
}
